import { Auth } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';

import { STUDIO_REFERRER } from 'src/routes/onboarding/setup/constants';
import { getAuthRequestInfo, GetSignInMetadata } from 'src/utils/AuthUtils';
import { ensureError } from 'src/utils/Errors';
import { removeSpaceToLowerCase } from 'src/utils/StringUtils';
import { generatePassword } from 'src/utils/UserUtils';

export const STUDIO_ACCOUNT_ALREADY_EXISTS_CODE =
  'StudioAccountAlreadyExistsException';

// Placeholder names satisfy Cognito's required attributes until workspace setup
// collects the user's real name; treated as "no real name" when prefilling.
export const STUDIO_PLACEHOLDER_GIVEN_NAME = 'Studio';
export const STUDIO_PLACEHOLDER_FAMILY_NAME = 'User';

// Cognito's signUp API requires a password even when users never set one.
// Studio onboarding is passwordless from the user's perspective: we generate
// a random password here and never surface it in the UI.
const STUDIO_SIGNUP_PASSWORD_LENGTH = 16;

const getStudioClientMetadata = async () => {
  const requestInfo = await getAuthRequestInfo();
  return {
    ...GetSignInMetadata(undefined, requestInfo),
    referrer: STUDIO_REFERRER,
  };
};

const getCognitoErrorCode = (error: unknown): string | undefined => {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    return (error as { code: string }).code;
  }

  return ensureError(error).name;
};

// Matches Cognito's "already confirmed" errors from both confirmSignUp
// (NotAuthorizedException: "...Current status is CONFIRMED") and resendSignUp
// (InvalidParameterException: "User is already confirmed."). Retrying
// confirmation after a mid-flow failure lands on an already-confirmed account,
// which we treat as success so the flow can recover instead of getting stuck.
const isAlreadyConfirmedError = (error: unknown) => {
  const message = ensureError(error).message.toLowerCase();

  return (
    message.includes('already confirmed') ||
    message.includes('current status is confirmed')
  );
};

const createStudioAccountAlreadyExistsError = () =>
  Object.assign(new Error(STUDIO_ACCOUNT_ALREADY_EXISTS_CODE), {
    code: STUDIO_ACCOUNT_ALREADY_EXISTS_CODE,
  });

// Sign-out is best-effort cleanup; a failure here must never fail an otherwise
// successful verification.
const signOutQuietly = async () => {
  try {
    await Auth.signOut();
  } catch {
    // ignore
  }
};

const DELETE_USER_RETRY_ATTEMPTS = 2;

// Removes the currently signed-in Cognito user — the just-created UUID account —
// when the email turns out to belong to an existing account. Retries transient
// failures and, if it still can't delete the account, signs out and rethrows so
// the caller surfaces the failure instead of masking it as a clean "account
// already exists"; masking it would let retries accumulate orphan confirmed users.
const deleteCurrentUserOrThrow = async () => {
  let lastError: unknown;
  for (let attempt = 1; attempt <= DELETE_USER_RETRY_ATTEMPTS; attempt += 1) {
    try {
      await Auth.deleteUser();
      return;
    } catch (error) {
      lastError = error;
    }
  }
  await signOutQuietly();
  throw ensureError(lastError);
};

export const getStudioVerificationErrorMessage = (error: unknown) => {
  const errorCode = getCognitoErrorCode(error);

  if (errorCode === 'CodeMismatchException') {
    return 'Incorrect code — try again.';
  }
  if (errorCode === 'ExpiredCodeException') {
    return 'This code has expired. Resend a new code and try again.';
  }

  return (
    ensureError(error).message || 'Unable to verify code. Please try again.'
  );
};

export interface StudioSignUpResult {
  // The Cognito username — a UUID, not the email (see below).
  cognitoUsername: string;
  email: string;
  userConfirmed: boolean;
  password: string;
}

export const studioSignUpWithEmail = async (
  email: string,
): Promise<StudioSignUpResult> => {
  const normalizedEmail = removeSpaceToLowerCase(email);
  const clientMetadata = await getStudioClientMetadata();
  const password = generatePassword(STUDIO_SIGNUP_PASSWORD_LENGTH);
  // Studio internal users get a UUID Cognito username, matching how every other
  // internal user is created. The GetStream user id is derived from the Cognito
  // username (`portalID_username`) and GetStream rejects characters like the `+`
  // in plus-addressed emails, so using the email as the username produced an
  // illegal id that silently broke messaging (POR-21260). The email is attached
  // as an attribute now and promoted to the `preferred_username` sign-in alias
  // after confirmation — Cognito rejects setting an alias on an unconfirmed
  // account, so it cannot be done here.
  const cognitoUsername = uuidv4();
  const signUpResult = await Auth.signUp({
    username: cognitoUsername,
    password,
    attributes: {
      email: normalizedEmail,
      given_name: STUDIO_PLACEHOLDER_GIVEN_NAME,
      family_name: STUDIO_PLACEHOLDER_FAMILY_NAME,
    },
    clientMetadata,
  });

  return {
    cognitoUsername,
    email: normalizedEmail,
    userConfirmed: signUpResult.userConfirmed,
    password,
  };
};

// confirmStudioEmailVerification confirms the account and then promotes the email
// to the `preferred_username` sign-in alias so email-based login and the backend's
// AdminGetUser(email) linking during workspace setup resolve to this user. Setting
// the alias requires an authenticated session on a confirmed account, so we sign in
// with the UUID username, set the attribute, then sign out — workspace setup signs
// in again after the DynamoDB record exists so the issued token carries the level
// claim (the pre-token trigger can only inject it once the record is present).
export const confirmStudioEmailVerification = async (
  cognitoUsername: string,
  code: string,
  email: string,
  password: string,
) => {
  // Tolerate an already-confirmed account so a retry after a mid-flow failure
  // (e.g. sign-in or attribute update threw) re-runs the alias promotion below
  // instead of dead-ending on "User cannot be confirmed".
  try {
    await Auth.confirmSignUp(cognitoUsername, code);
  } catch (error) {
    if (!isAlreadyConfirmedError(error)) {
      throw error;
    }
  }

  const requestInfo = await getAuthRequestInfo();
  const user = await Auth.signIn(
    cognitoUsername,
    password,
    GetSignInMetadata(undefined, requestInfo),
  );

  try {
    // Promoting the email to the preferred_username alias is idempotent: a retry
    // sets the same value on the same user, so it only throws AliasExists when
    // the email already belongs to a different (existing) account.
    await Auth.updateUserAttributes(user, { preferred_username: email });
  } catch (error) {
    if (getCognitoErrorCode(error) === 'AliasExistsException') {
      // The email already belongs to another confirmed account. Delete this
      // just-created UUID account (we're signed in as it) so retries don't leave
      // orphan confirmed users behind, then surface the conflict. If the delete
      // itself fails, deleteCurrentUserOrThrow rethrows rather than masking it.
      await deleteCurrentUserOrThrow();
      throw createStudioAccountAlreadyExistsError();
    }
    throw error;
  }

  // Sign out so workspace setup signs in again after the DynamoDB record exists,
  // yielding a token with the portal/level claims (the pre-token trigger only
  // injects them once the record is present). Best-effort — see signOutQuietly.
  await signOutQuietly();
};

export const resendStudioEmailVerificationCode = async (
  cognitoUsername: string,
) => {
  const clientMetadata = await getStudioClientMetadata();
  await Auth.resendSignUp(cognitoUsername, clientMetadata);
};
